import { camelize, camelizeKeys } from 'humps';
import { get } from 'lodash-es';

import enumTypes, { sourceTypeEnum } from '@/app/library/constants';
import { constants, MEDIA_CARD_FORMAT, SOURCE_TO_METRIC_PROP } from '@/config';
import { localize } from '@/utils/formatters';
import { and, not, type Predicate } from '@/utils/predicate-utils';

interface ResolvedMetricValue {
  value: unknown;
  format: Record<string, unknown> | null;
}

/** Metrics backed by Instagram collaborator insights; all other metrics belong to the author. */
const COLLAB_SUPPORTED_METRICS = new Set([
  'commentsCount',
  'engagement',
  'likeCount',
  'paidAndOrganicShares',
  'reposts',
  'saved',
  'totalEngagement',
]);

const scaledPercentageMetrics = [
  'averageCompletionRate',
  'avgViewPercentage',
  'cardClickRate',
  'cardTeaserClickRate',
  'clickThroughRate',
  'completionRate',
  `completionRateViews`,
  'effectiveness',
  'effectivenessEngagements',
  'engagement',
  'engagementRate',
  'engagementRateImpressions',
  'engagementRatePublic',
  'exitRate',
  'exitRateViews',
  'fullVideoWatchedRate',
  'impressionsForYouRate',
  'reel.effectiveness',
  'ugcEffectiveness',
  'organicEffectiveness',
  'organicEngagementRate',
  'paidEngagementRate',
  'paidTotalEngagementRate',
  'paidAndOrganicEngagementRate',
  'paidAndOrganicEffectivenessV2',
  'paidAndOrganicEngagementRateV2',
  'impressionsPersonalProfileRate',
  'impressionsSearchRate',
  'impressionsSoundRate',
  'impressionsFollowRate',
  'impressionsHashtagRate',
  'engagementRateViews',
  'organicEffectivenessV2',
  'paidEffectivenessV2',
  'paidAndOrganicEffectivenessV2',
  'organicEngagementRateV2',
  'paidEngagementRateV2',
  'paidAndOrganicEngagementRateV2',
  'viewsBasedEngagementRate',
  'reelsSkipRate',
];
const directPercentageMetrics = ['ctr'];
const secondTimeMetrics = [
  'averageTimeWatched',
  'avgViewDuration',
  'estSecondsWatched',
  'estPremiumSecondsWatched',
  'totalTimeWatched',
  'totalTimeViewedSec',
  'totalTimeViewedSecLong',
  'duration',
  'avgViewTimeSeconds',
  'viewTimeSeconds',
  'avgTimeViewedSec',
];
const millisecondTimeMetrics = [
  'averageVideoWatchTime',
  'videoAnalyticsTimeWatchedForViews',
  'videoAnalytics.timeWatchedForViews',
  'reel.post_video_view_time',
  'reel.postVideoAvgTimeWatched',
  'reel.postVideoViewTime',
];
const twoDecimalMetrics = ['entertainmentScore', 'frequency'];
const monetaryMetrics = [
  'cpc',
  'cpm',
  'amountSpend',
  'amountSpent',
  'costPerThruplays',
  'costPerThruplay',
];

type MediaCardSourceData = {
  source?: string | null;
};

export function getMediaCardChannelKey(media?: MediaCardSourceData | null): string | undefined {
  const source = media?.source ?? undefined;
  if (!source) return undefined;
  return (
    (SOURCE_TO_METRIC_PROP as Record<string, string | undefined>)[source] || source.toLowerCase()
  );
}

/**
 * Mirrors `useMediaData`'s `sourceData`: camelized media with its channel block
 * hoisted to the top level, so a metric `attribute` resolves regardless of nesting.
 */
export function getMediaCardSourceData(media: Record<string, unknown>): Record<string, unknown> {
  const camelMedia = camelizeKeys(media) as Record<string, unknown>;
  const channelKey = getMediaCardChannelKey(media as { source?: string | null });
  return {
    ...camelMedia,
    ...(camelMedia[camelize(channelKey ?? '')] as Record<string, unknown> | undefined),
  };
}

interface ChannelMetricRuleContext {
  sourceData: Record<string, unknown>;
  metricName: string;
}

type MetricMatcher = Predicate<ChannelMetricRuleContext>;

interface ChannelMetricRule {
  /** Whether this rule applies to the requested metric and media. */
  when: MetricMatcher;
  /** Replacement value; `undefined` suppresses the metric. */
  resolve: (context: ChannelMetricRuleContext) => unknown;
}

const matchesMetricIn =
  (metricNames: ReadonlySet<string>): MetricMatcher =>
  ({ metricName }) =>
    metricNames.has(metricName);

const isInstagramCollab: MetricMatcher = ({ sourceData }) =>
  sourceData.sourceType === sourceTypeEnum.COLLAB ||
  sourceData.brandMediaType === enumTypes.INSTAGRAM_COLLAB;

/** Per-channel metric overrides; the first rule matching a metric wins. */
const CHANNEL_METRIC_RULES: Record<string, ChannelMetricRule[]> = {
  [constants.INSTAGRAM.toLowerCase()]: [
    {
      // Never render public/author values for collaborator posts.
      when: and(not(matchesMetricIn(COLLAB_SUPPORTED_METRICS)), isInstagramCollab),
      resolve: () => undefined,
    },
    {
      // Unowned media has no private effectivenessEngagements; fall back to public effectiveness.
      when: and(
        matchesMetricIn(new Set(['effectivenessEngagements'])),
        ({ sourceData, metricName }) =>
          !get(sourceData, metricName) && sourceData.sourceType !== sourceTypeEnum.OWNED,
      ),
      resolve: ({ sourceData }) => sourceData.effectiveness,
    },
    {
      // LikeShop clicks live under the `likeshop` block, not the channel block.
      when: matchesMetricIn(new Set(['likeshopClicks'])),
      resolve: ({ sourceData }) => get(sourceData, 'likeshop.clicks'),
    },
  ],
};

/**
 * Resolve the display value + `MEDIA_CARD_FORMAT` (or null) for one metric,
 * shared by the media card and the board detail list view so both format
 * identically. `formatCurrency` is injected because it needs the platform store.
 */
export function resolveMediaCardMetricValue(
  sourceData: Record<string, unknown>,
  metricName: string,
  { formatCurrency }: { formatCurrency?: (value: unknown) => string } = {},
): ResolvedMetricValue {
  // `metricName` can be a path to the value e.g. reel.fbReelsTotalPlays
  let metricValue: unknown = get(sourceData, metricName);
  const channelKey = getMediaCardChannelKey(sourceData as MediaCardSourceData);
  const ruleContext = { sourceData, metricName };

  for (const rule of CHANNEL_METRIC_RULES[channelKey ?? ''] ?? []) {
    if (rule.when(ruleContext)) {
      metricValue = rule.resolve(ruleContext);
      break;
    }
  }

  // Edge case where some metrics are -1 when they should be undefined.
  if (metricValue === -1) metricValue = undefined;

  let format: ResolvedMetricValue['format'] = MEDIA_CARD_FORMAT.NUMBER;

  if (metricValue == null) {
    format = null;
  } else if (scaledPercentageMetrics.includes(metricName)) {
    format = MEDIA_CARD_FORMAT.PERCENTAGE;
  } else if (directPercentageMetrics.includes(metricName)) {
    format = null;
    metricValue = (metricValue as number).toFixed(2);
  } else if (twoDecimalMetrics.includes(metricName)) {
    format = MEDIA_CARD_FORMAT.TWO_DECIMAL;
  } else if (secondTimeMetrics.includes(metricName)) {
    format = MEDIA_CARD_FORMAT.DURATION;
  } else if (millisecondTimeMetrics.includes(metricName)) {
    format = MEDIA_CARD_FORMAT.DURATION_MILLISECONDS;
  } else if (monetaryMetrics.includes(metricName)) {
    format = null;
    metricValue = formatCurrency ? formatCurrency(metricValue) : metricValue;
  } else if (metricName === 'emv') {
    format = null;
    metricValue = `$${localize(metricValue as number)}`;
  } else if (typeof metricValue === 'string' && Number.isNaN(Number(metricValue))) {
    format = null;
  }

  return { value: metricValue, format };
}
